const SITE_URL = "https://georgiadream.ge";

interface Pool {
  name: string;
  type?: string;
  description?: string;
  image?: string;
  image_alt?: string;
}

interface Restaurant {
  name: string;
  cuisine?: string;
  description?: string;
  location?: string;
  hours?: string;
  hours_seasonal?: boolean;
  menu_url?: string;
  image?: string;
  image_alt?: string;
}

interface QuickFact {
  icon?: string;
  label?: string;
  value?: string;
}

export interface AmenitiesPage {
  title?: string;
  seo_title?: string;
  description?: string;
  meta_description?: string;
  hero_image?: string;
  pool_and_amenity_costs_updated?: string;
  pool_showcase?: {
    header?: string;
    compare_intro?: string;
    pools?: Pool[];
    quick_facts?: QuickFact[];
  };
  beach_showcase?: {
    header?: string;
    compare_intro?: string;
    image?: string;
    image_alt?: string;
  };
  aquapark_showcase?: {
    header?: string;
    intro?: string;
    image?: string;
    image_alt?: string;
    quick_facts?: QuickFact[];
  };
  restaurants_showcase?: {
    header?: string;
    intro?: string;
    items?: Restaurant[];
  };
}

export interface AmenitiesSchemaConfig {
  pagePath: string;   // e.g. "/amenities" or "/ru/amenities"
  homePath: string;   // e.g. "/" or "/ru"
  homeLabel: string;  // localized "Home"
  pageTitle?: string;
}

function abs(src?: string): string | undefined {
  if (!src) return undefined;
  return src.startsWith("http") ? src : `${SITE_URL}${src}`;
}

function slugify(name: string): string {
  const ascii = name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "");
  const slug = ascii.replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  // Fallback for non-Latin names (Cyrillic/Georgian): use hex-length hash.
  if (!slug) {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
    return `item-${Math.abs(hash).toString(16)}`;
  }
  return slug;
}

function parseTimeRange(range?: string): { opens: string; closes: string } | null {
  if (!range) return null;
  const m = range.match(/(\d{1,2}:\d{2})\s*[–\-—]\s*(\d{1,2}:\d{2})/);
  if (!m) return null;
  const closes = m[2] === "00:00" ? "23:59" : m[2];
  return { opens: m[1], closes };
}

const ALL_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

function buildOpeningHours(range?: string, validFrom?: string, validThrough?: string) {
  const parsed = parseTimeRange(range);
  if (!parsed) return undefined;
  return {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ALL_DAYS,
    opens: parsed.opens,
    closes: parsed.closes,
    ...(validFrom ? { validFrom } : {}),
    ...(validThrough ? { validThrough } : {}),
  };
}

export function buildAmenitiesLd(page: AmenitiesPage, cfg: AmenitiesSchemaConfig) {
  const pageUrl = `${SITE_URL}${cfg.pagePath}`;
  const homeUrl =
    cfg.homePath === "/" ? `${SITE_URL}/` : `${SITE_URL}${cfg.homePath}/`;
  const lodgingId = `${SITE_URL}/#lodgingbusiness`;
  const dateModified = page.pool_and_amenity_costs_updated;

  const graph: Array<Record<string, unknown>> = [];

  // 1. Breadcrumb
  graph.push({
    "@type": "BreadcrumbList",
    "@id": `${pageUrl}#breadcrumbs`,
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: cfg.homeLabel,
        item: homeUrl,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: cfg.pageTitle || page.title || "Amenities",
        item: pageUrl,
      },
    ],
  });

  // 2. WebPage
  graph.push({
    "@type": "WebPage",
    "@id": `${pageUrl}#webpage`,
    url: pageUrl,
    name: cfg.pageTitle || page.title,
    description: page.meta_description || page.description,
    ...(dateModified ? { dateModified } : {}),
    breadcrumb: { "@id": `${pageUrl}#breadcrumbs` },
    about: { "@id": lodgingId },
  });

  // 3. Pools (each as TouristAttraction)
  const poolHoursFact = page.pool_showcase?.quick_facts?.find(
    (f) => f.icon === "clock",
  );
  const poolOpeningHoursSpec = buildOpeningHours(poolHoursFact?.value);
  for (const pool of page.pool_showcase?.pools ?? []) {
    graph.push({
      "@type": "TouristAttraction",
      "@id": `${pageUrl}#pool-${slugify(pool.name)}`,
      name: pool.name,
      ...(pool.description ? { description: pool.description } : {}),
      ...(pool.image ? { image: abs(pool.image) } : {}),
      containedInPlace: { "@id": lodgingId },
      isAccessibleForFree: false,
      ...(poolOpeningHoursSpec
        ? { openingHoursSpecification: poolOpeningHoursSpec }
        : {}),
    });
  }

  // 4. Aquapark
  if (page.aquapark_showcase) {
    const ap = page.aquapark_showcase;
    const hoursFact = ap.quick_facts?.find((f) => f.icon === "clock");
    const hasSeason = !!ap.quick_facts?.find((f) => f.icon === "calendar");
    const openingHoursSpec = buildOpeningHours(
      hoursFact?.value,
      hasSeason ? "2026-06-01" : undefined,
      hasSeason ? "2026-09-30" : undefined,
    );
    graph.push({
      "@type": "TouristAttraction",
      "@id": `${pageUrl}#aquapark`,
      name: ap.header || "Aquapark",
      ...(ap.intro ? { description: ap.intro } : {}),
      ...(ap.image ? { image: abs(ap.image) } : {}),
      containedInPlace: { "@id": lodgingId },
      ...(openingHoursSpec ? { openingHoursSpecification: openingHoursSpec } : {}),
    });
  }

  // 5. Beach
  if (page.beach_showcase) {
    graph.push({
      "@type": "Beach",
      "@id": `${pageUrl}#beach`,
      name: page.beach_showcase.header || "Resort beach",
      ...(page.beach_showcase.compare_intro
        ? { description: page.beach_showcase.compare_intro }
        : {}),
      ...(page.beach_showcase.image ? { image: abs(page.beach_showcase.image) } : {}),
      containedInPlace: { "@id": lodgingId },
    });
  }

  // 6. Restaurants
  for (const venue of page.restaurants_showcase?.items ?? []) {
    const openingHoursSpec = !venue.hours_seasonal
      ? buildOpeningHours(venue.hours)
      : undefined;
    graph.push({
      "@type": "Restaurant",
      "@id": `${pageUrl}#restaurant-${slugify(venue.name)}`,
      name: venue.name,
      ...(venue.description ? { description: venue.description } : {}),
      ...(venue.image ? { image: abs(venue.image) } : {}),
      ...(venue.cuisine ? { servesCuisine: venue.cuisine } : {}),
      ...(venue.menu_url ? { menu: venue.menu_url } : {}),
      ...(openingHoursSpec ? { openingHoursSpecification: openingHoursSpec } : {}),
      containedInPlace: { "@id": lodgingId },
    });
  }

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}
